import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();
  const { refreshUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: password });
      localStorage.setItem("netcrm_token", r.data.token);
      await refreshUser();
      setDone(true);
      setTimeout(() => nav("/dashboard"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
        <div className="text-center">
          <div className="overline mb-3">// invalid link</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">No reset token</h1>
          <Link to="/forgot-password" className="btn-primary mt-6 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" style={{ background: "var(--bg)" }} data-testid="reset-password-page">
      <Toaster theme="dark" />
      <div className="hidden md:block relative grid-bg border-r" style={{ borderColor: "var(--border)" }}>
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <Link to="/" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <div>
            <div className="overline mb-3">// almost there</div>
            <h2 className="font-display font-black text-5xl tracking-tighter leading-none">
              New password.<br />Fresh start.
            </h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        {done ? (
          <div className="w-full max-w-md text-center">
            <CheckCircle size={64} weight="duotone" color="var(--accent)" className="mx-auto" />
            <div className="overline mt-6 mb-2" style={{ color: "var(--accent)" }}>// success</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Password updated.</h1>
            <p className="mt-3" style={{ color: "var(--text-2)" }}>Signing you in...</p>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-form">
            <div className="overline mb-4">// set new password</div>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-10">Choose a new one.</h1>

            <label className="label-base">New password</label>
            <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-base mb-6" autoComplete="new-password" />

            <label className="label-base">Confirm password</label>
            <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-base mb-8" autoComplete="new-password" />

            <button type="submit" disabled={busy} data-testid="reset-submit" className="btn-primary w-full flex items-center justify-between">
              {busy ? "Setting password..." : "Set new password"} <ArrowRight weight="bold" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
